import { useEffect, useState } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Euro, CheckCircle2, AlertCircle, Car } from "lucide-react";
import { formatCurrency } from "@/lib/api";
import { toast } from "sonner";
import { DatePicker } from "@/components/DatePicker";

export default function BolloUpdate() {
  const nav = useNavigate();
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newDate, setNewDate] = useState("");
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const apiBase = (process.env.REACT_APP_BACKEND_URL || "") + "/api";
    axios.get(`${apiBase}/public/bollo/${token}`)
      .then((res) => {
        setData(res.data);
        if (res.data?.bollo_amount) setAmount(String(res.data.bollo_amount));
      })
      .catch((e) => setError(e?.response?.data?.detail || "Link non valido"))
      .finally(() => setLoading(false));
  }, [token]);

  const submit = async () => {
    if (!newDate) { toast.error("Seleziona la nuova data di scadenza bollo"); return; }
    setBusy(true);
    try {
      const apiBase = (process.env.REACT_APP_BACKEND_URL || "") + "/api";
      await axios.post(`${apiBase}/public/bollo/${token}`, {
        bollo_due_date: newDate,
        bollo_amount: amount ? Number(amount) : null,
      });
      setDone(true);
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  const minDateISO = (() => {
    const t = new Date(); t.setDate(t.getDate() + 1);
    return t.toISOString().slice(0, 10);
  })();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-400" data-testid="bollo-loading">
        <Loader2 className="h-6 w-6 animate-spin mr-2" /> Caricamento…
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-slate-50">
        <Card className="max-w-md w-full" data-testid="bollo-error">
          <CardContent className="pt-8 pb-6 text-center">
            <AlertCircle className="h-12 w-12 text-rose-500 mx-auto mb-3" />
            <h2 className="font-display text-xl font-bold mb-2">Link non valido</h2>
            <p className="text-sm text-slate-600">{error}</p>
            <p className="text-xs text-slate-400 mt-4">Se il link è scaduto, contatta la tua agenzia.</p>
          </CardContent>
        </Card>
      </div>
    );
  }
  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-gradient-to-br from-emerald-50 via-white to-blue-50">
        <Card className="max-w-md w-full" data-testid="bollo-success">
          <CardContent className="pt-8 pb-6 text-center">
            <CheckCircle2 className="h-14 w-14 text-emerald-500 mx-auto mb-3" />
            <h2 className="font-display text-xl font-bold mb-2">Data aggiornata!</h2>
            <p className="text-sm text-slate-600 mb-2">
              Grazie {data?.client_name?.trim() || ""}. La nuova scadenza del bollo è stata salvata.
              Ti invieremo un nuovo promemoria 30 e 7 giorni prima.
            </p>
            <Button onClick={() => nav("/")} className="mt-4 bg-emerald-600 hover:bg-emerald-700">Chiudi</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-slate-50 flex items-center justify-center p-4">
      <Card className="max-w-md w-full" data-testid="bollo-page">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 font-display text-xl">
            <Euro className="h-6 w-6 text-orange-600" /> Aggiorna scadenza bollo
          </CardTitle>
          <p className="text-xs text-slate-500">
            Ciao <strong>{data?.client_name || ""}</strong>, inserisci la nuova data di scadenza
            del bollo del tuo veicolo per continuare a ricevere i promemoria.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="rounded-lg border border-orange-200 bg-orange-50/60 p-3 text-sm">
            <div className="flex items-center gap-2 font-semibold text-orange-900 mb-1">
              <Car className="h-4 w-4" /> Il tuo veicolo
            </div>
            <div className="text-xs text-orange-800 space-y-0.5">
              <div>Targa: <strong className="font-mono">{data?.vehicle_plate || "—"}</strong></div>
              <div>Modello: <strong>{[data?.vehicle_brand, data?.vehicle_model].filter(Boolean).join(" ") || "—"}</strong></div>
              {data?.policy_number && <div>Polizza: <strong>{data.policy_number}</strong></div>}
            </div>
          </div>
          {data?.current_bollo_date && (
            <p className="text-xs text-slate-500">
              Scadenza precedente: <strong>{new Date(data.current_bollo_date).toLocaleDateString("it-IT")}</strong>
              {data?.bollo_amount && <> · <strong>{formatCurrency(data.bollo_amount)}</strong></>}
            </p>
          )}
          <div>
            <Label>Nuova data scadenza bollo <span className="text-red-500">*</span></Label>
            <DatePicker data-testid="bollo-new-date" min={minDateISO}
              value={newDate}
              onChange={(e) => setNewDate(e.target.value)}
              className="font-mono" />
          </div>
          <div>
            <Label>Importo bollo (facoltativo)</Label>
            <Input
              data-testid="bollo-amount"
              type="number"
              step="0.01"
              min="0"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="Es. 215.50"
            />
            <p className="text-[11px] text-slate-400 mt-1">
              💡 Se non aggiorni la data, il sistema smetterà di inviarti promemoria.
            </p>
          </div>
          <Button
            data-testid="bollo-submit"
            onClick={submit}
            disabled={busy || !newDate}
            className="w-full bg-orange-600 hover:bg-orange-700 text-white"
          >
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
            Conferma nuova data
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
